import React from "react";

import { StatusDetail } from "../../../common/components";
import { StatusEntry } from "../../../common/components/StatusDetail";
import { PaymentStatus, PaymentOrderStatus } from "../../../types/payment";
import { HistoryItem } from "../../../types/common";
import { UserRole } from "../../../types/userCurrent";
import { buildUniqueStatusHistory } from "../../../utils/common";

import PreAndPaymentStatusBadge from "./PreAndPaymentStatusBadge";
import { ApproveStatus } from "../../../types/claim";

type PreAndPaymentStatusDetailProps = {
    roles?: UserRole[];
    preSubmitPaymentHistory: HistoryItem<PaymentOrderStatus>[];
    latestPreSubmitPaymentStatus: PaymentOrderStatus;
    latestPaymentStatus?: PaymentStatus | null;
    approverStatus?: ApproveStatus;
    closed: boolean;
    isPreviousPriorityApproverApproved?: boolean | null;
};

const PreAndPaymentStatusDetail = ({
    roles,
    preSubmitPaymentHistory,
    latestPreSubmitPaymentStatus,
    latestPaymentStatus,
    approverStatus,
    closed,
    isPreviousPriorityApproverApproved,
}: PreAndPaymentStatusDetailProps): JSX.Element => {
    return (
        <StatusDetail
            className="mt-1 fs-7 fw-normal float-end"
            statusEntries={toStatusEntries(preSubmitPaymentHistory, closed)}
        >
            <PreAndPaymentStatusBadge
                roles={roles}
                paymentOrderStatus={latestPreSubmitPaymentStatus}
                paymentStatus={latestPaymentStatus}
                approverStatus={approverStatus}
                showActionRequired={!closed}
                isPreviousPriorityApproverApproved={
                    isPreviousPriorityApproverApproved
                }
            />
        </StatusDetail>
    );
};

const toStatusEntries = (
    preSubmitPaymentHistory: HistoryItem<PaymentOrderStatus>[],
    closed: boolean,
): StatusEntry[] => {
    const uniqueStatusHistory = buildUniqueStatusHistory(preSubmitPaymentHistory);

    const isAtLeastOnePaymentRejected = uniqueStatusHistory.some(
        (it) => it.status === PaymentOrderStatus.REJECTED,
    );

    const isAllPaymentApproved = uniqueStatusHistory.some(
        (it) => it.status === PaymentOrderStatus.ALL_APPROVED,
    );

    const finalUniqueStatusHistory = (() => {
        switch (true) {
            case isAllPaymentApproved:
            case isAtLeastOnePaymentRejected:
                return uniqueStatusHistory.filter(
                    (it) => it.status !== PaymentOrderStatus.PARTIAL_APPROVED,
                );
            default:
                return uniqueStatusHistory;
        }
    })();

    const entries = finalUniqueStatusHistory.map((history): StatusEntry => {
        switch (history.status) {
            case PaymentOrderStatus.PARTIAL_APPROVED:
                return {
                    role: "Payment Order Approvers",
                    date: null,
                    status: "CTA",
                    badgeType: null,
                };
            case PaymentOrderStatus.CREATED:
                return {
                    role: "Finance Department",
                    date: history.timestamp,
                    status: "Payment Created",
                    badgeType: "success",
                };
            case PaymentOrderStatus.REJECTED:
                return {
                    role: "Payment Order Approvers",
                    date: history.timestamp,
                    status: "Payment Rejected",
                    badgeType: "danger",
                };
            default:
                return {
                    role: "Payment Order Approvers",
                    date: history.timestamp,
                    status: "Payment Approved",
                    badgeType: "success",
                };
        }
    });
    let firstEntry: StatusEntry | null = null;
    if (!closed) {
        switch (preSubmitPaymentHistory[0]?.status) {
            case PaymentOrderStatus.CREATED:
                firstEntry = {
                    role: "Payment Order Approvers",
                    date: null,
                    status: "CTA",
                    badgeType: null,
                };
                break;
        }
    }
    return firstEntry ? [firstEntry].concat(entries) : entries;
};

export default PreAndPaymentStatusDetail;
